import React from "react";
import { C, FontLink } from "./constants";

const s = {
  page:    { minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'DM Sans',sans-serif" },
  header:  { padding:"20px 24px 16px", borderBottom:`1px solid rgba(255,255,255,0.06)`, display:"flex", alignItems:"center", gap:10 },
  logo:    { fontSize:22 },
  siteName:{ fontSize:16, fontWeight:700, color:C.text },
  backLink:{ marginLeft:"auto", fontSize:13, color:C.purple, textDecoration:"none", fontWeight:600 },
  body:    { maxWidth:720, margin:"0 auto", padding:"40px 24px 80px" },
  h1:      { fontSize:26, fontWeight:800, color:C.text, marginBottom:6 },
  date:    { fontSize:12, color:C.text3, marginBottom:36 },
  h2:      { fontSize:15, fontWeight:700, color:C.text, marginTop:32, marginBottom:10 },
  h3:      { fontSize:13, fontWeight:700, color:C.text2, marginTop:20, marginBottom:8 },
  p:       { fontSize:14, color:C.text2, lineHeight:1.7, marginBottom:12 },
  ul:      { paddingLeft:20, marginBottom:12 },
  li:      { fontSize:14, color:C.text2, lineHeight:1.7, marginBottom:4 },
  highlight:{ background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.15)", borderRadius:10, padding:"14px 16px", marginBottom:16 },
  footer:  { textAlign:"center", padding:"32px 24px", borderTop:`1px solid rgba(255,255,255,0.06)`, marginTop:40 },
  footerText:{ fontSize:12, color:C.text3 },
  footerLink:{ color:C.purple, textDecoration:"none" },
};

function Header() {
  return (
    <div style={s.header}>
      <FontLink />
      <span style={s.logo}>🌙</span>
      <span style={s.siteName}>Barakah Budget</span>
      <a href="/" style={s.backLink}>← Open App</a>
    </div>
  );
}

function Footer() {
  return (
    <div style={s.footer}>
      <p style={s.footerText}>
        © 2026 Mufeed Muneer · <a href="/privacy" style={s.footerLink}>Privacy Policy</a> · <a href="/terms" style={s.footerLink}>Terms of Use</a> · <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>Contact</a>
      </p>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div style={s.page}>
      <Header />
      <div style={s.body}>
        <h1 style={s.h1}>Privacy Policy</h1>
        <p style={s.date}>Effective date: May 23, 2026 · Last updated: May 23, 2026</p>

        <div style={s.highlight}>
          <p style={{ ...s.p, marginBottom:0, color:C.text, fontWeight:600 }}>
            Short version: Your financial data stays on your device. We never see it, never receive it, and never sell it. There is nothing to sell.
          </p>
        </div>

        <h2 style={s.h2}>Who we are</h2>
        <p style={s.p}>Barakah Budget is a personal Islamic finance app developed and operated by Mufeed Muneer ("we," "us," or "our"), a sole proprietor based in Canada. Contact us at: <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>hello@barakahbudget.com</a></p>

        <h2 style={s.h2}>What data we collect — and where it lives</h2>

        <h3 style={s.h3}>Data stored only on your device</h3>
        <p style={s.p}>All financial data you enter into Barakah Budget is stored exclusively on your device. This includes:</p>
        <ul style={s.ul}>
          {["Transactions (income, expenses, amounts, dates, categories, notes)","Budgets and spending limits","Savings goals","Debt records","Zakat calculations and payment history","Recurring bill records","Net worth adjustments","Your name and currency preference"].map(item => (
            <li key={item} style={s.li}>{item}</li>
          ))}
        </ul>
        <p style={s.p}><strong>We do not have servers that receive, store, or process your financial data. It never leaves your device.</strong></p>

        <h3 style={s.h3}>Data we do NOT collect</h3>
        <ul style={s.ul}>
          {["We do not collect your financial data","We do not use analytics SDKs (no Firebase, no Mixpanel, no Amplitude)","We do not track your behaviour within the app","We do not collect your location","We do not access your contacts, camera, or microphone","We do not run targeted advertising","We do not sell data to third parties — there is no data to sell"].map(item => (
            <li key={item} style={s.li}>{item}</li>
          ))}
        </ul>

        {/* AI MONEY INSIGHTS SECTION — hidden until native app feedback confirms demand
        <h3 style={s.h3}>If you use Money Insights (web app only)</h3>
        <p style={s.p}>The Money Insights feature on the web app sends your message text and a brief financial summary (current savings balance, estimated zakat due, approximate spending totals) to Anthropic's Claude API. This data is:</p>
        <ul style={s.ul}>
          {["Used only to generate a response to your question","Subject to Anthropic's privacy policy (anthropic.com/privacy)","Not stored by us after the API call completes","Not used to train AI models (Anthropic's API terms)"].map(item => (
            <li key={item} style={s.li}>{item}</li>
          ))}
        </ul>
        <p style={s.p}>You control whether you use this feature. No financial data is sent unless you explicitly open Money Insights and type a message.</p>
        */}

        <h3 style={s.h3}>Third-party service providers (iOS app only)</h3>
        <p style={s.p}>The iOS app is built using <strong>Expo</strong> and distributed via <strong>Expo Application Services (EAS)</strong>. Expo may collect limited diagnostic data (crash reports, build metadata) subject to their privacy policy at expo.dev/privacy. This data is technical and does not include your financial information.</p>
        <p style={s.p}>In-app purchases are processed entirely by <strong>Apple</strong>. We do not receive or store your payment information. Apple's privacy policy governs payment processing: apple.com/legal/privacy</p>
        <p style={s.p}>Subscription management — verifying entitlements and restoring purchases — uses <strong>RevenueCat</strong>. RevenueCat receives purchase receipts and subscription status from Apple to confirm your Pro access. It does not receive your financial data. See RevenueCat's privacy policy at revenuecat.com/privacy.</p>

        <h3 style={s.h3}>Backup / Export</h3>
        <p style={s.p}>If you use the Export Backup feature, a JSON file containing all your app data is saved to your device. You control where this file goes. We never receive it.</p>

        <h2 style={s.h2}>Children's privacy</h2>
        <p style={s.p}>Barakah Budget is not directed at children under 13. We do not knowingly collect any information from children under 13.</p>

        <h2 style={s.h2}>Data retention and deletion</h2>
        <p style={s.p}>Your data exists only on your device. To delete all data:</p>
        <ul style={s.ul}>
          <li style={s.li}>Go to Settings → Danger Zone → Delete All Data within the app, or</li>
          <li style={s.li}>Delete the Barakah Budget app from your device</li>
        </ul>
        <p style={s.p}>Either action permanently removes all data. We have no copy to delete on our end.</p>

        <h2 style={s.h2}>Changes to this policy</h2>
        <p style={s.p}>If we materially change this policy (for example, if we add analytics or server-side features in a future version), we will update the effective date and notify users within the app before the changes take effect.</p>

        <h2 style={s.h2}>Contact</h2>
        <p style={s.p}>Email: <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>hello@barakahbudget.com</a></p>
      </div>
      <Footer />
    </div>
  );
}

export function SupportPage() {
  return (
    <div style={s.page}>
      <Header />
      <div style={s.body}>
        <h1 style={s.h1}>Support</h1>
        <p style={s.date}>Barakah Budget · hello@barakahbudget.com</p>

        <div style={s.highlight}>
          <p style={{ ...s.p, marginBottom:0, color:C.text, fontWeight:600 }}>
            For support, email{" "}
            <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>hello@barakahbudget.com</a>
          </p>
        </div>

        <h2 style={s.h2}>Common questions</h2>

        <h3 style={s.h3}>How do I restore my Pro purchase?</h3>
        <p style={s.p}>Tap any locked Pro feature to open the upgrade screen, then tap "Restore Purchases" at the bottom. Your purchase is tied to your Apple ID — make sure you're signed in with the same Apple ID you used to subscribe.</p>

        <h3 style={s.h3}>How do I back up my data?</h3>
        <p style={s.p}>Settings tab → Data &amp; Backup → Export JSON. Save the file to your Files app or iCloud Drive. To restore on another device, use Import JSON with the same file.</p>

        <h3 style={s.h3}>How do I cancel my subscription?</h3>
        <p style={s.p}>iPhone Settings app → tap your name → Subscriptions → Barakah Budget → Cancel. You keep Pro access until the end of the current billing period.</p>

        <h3 style={s.h3}>Can I use Barakah Budget on multiple devices?</h3>
        <p style={s.p}>Data is stored locally on each device. Use the Export/Import feature to move your data between devices. Your Pro subscription covers all devices signed into the same Apple ID.</p>

        <h3 style={s.h3}>Is my financial data private?</h3>
        <p style={s.p}>Yes. All your data stays on your device — we never receive, store, or transmit your financial information. See our <a href="/privacy" style={s.footerLink}>Privacy Policy</a> for full details.</p>

        <h3 style={s.h3}>I reinstalled the app and my data is gone</h3>
        <p style={s.p}>Barakah Budget stores all data on your device — there is no account or cloud backup. If you deleted the app, the data was removed with it. To avoid this in future, use Settings → Data &amp; Backup → Export JSON before deleting.</p>

        <h2 style={s.h2}>Contact</h2>
        <p style={s.p}>Email: <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>hello@barakahbudget.com</a></p>
        <p style={s.p}>We typically respond within 24–48 hours.</p>
      </div>
      <Footer />
    </div>
  );
}

export function TermsPage() {
  return (
    <div style={s.page}>
      <Header />
      <div style={s.body}>
        <h1 style={s.h1}>Terms of Use</h1>
        <p style={s.date}>Effective date: May 23, 2026 · Last updated: May 23, 2026</p>

        <h2 style={s.h2}>1. Agreement</h2>
        <p style={s.p}>By using Barakah Budget ("the App"), you agree to these Terms of Use. If you do not agree, do not use the App. The App is operated by Mufeed Muneer ("we," "us," or "our"), a sole proprietor based in Canada.</p>

        <h2 style={s.h2}>2. What the App is</h2>
        <p style={s.p}>Barakah Budget is a personal finance tracking tool designed for Muslims. It helps you track income and expenses, estimate zakat obligations, monitor savings goals and debts, and track recurring bills.</p>
        <div style={s.highlight}>
          <p style={{ ...s.p, marginBottom:0, fontWeight:600, color:C.text }}>The App is a tool — it is not a financial advisor, Islamic scholar, or mufti. Nothing in the App constitutes financial advice, legal advice, or Islamic legal rulings (fatwas).</p>
        </div>

        <h2 style={s.h2}>3. Islamic finance disclaimer</h2>
        <p style={s.p}>Zakat calculations are <strong>estimates only</strong>, based on simplified formulas that may not reflect current precious metal prices, your specific madhab's rulings, complex asset situations, or scholarly disagreements on contemporary financial instruments.</p>
        <p style={s.p}><strong>Consult a qualified Islamic scholar for your specific zakat situation.</strong></p>
        <p style={s.p}>The App does not endorse any riba-based financial product. Labeling a debt as "Interest-based" is a factual description, not an endorsement.</p>

        <h2 style={s.h2}>4. No financial advice</h2>
        <p style={s.p}>Nothing in the App is financial advice. All projections and estimates (payoff dates, goal deadlines, zakat amounts) are for illustrative purposes only. Actual results will vary.</p>

        <h2 style={s.h2}>5. Your data</h2>
        <p style={s.p}>All financial data is stored on your device only. You are responsible for keeping your device secure, making backups, and not sharing your device with people you do not want to have access to your financial information. We cannot recover data you delete or lose.</p>

        <h2 style={s.h2}>6. Barakah Pro subscription</h2>
        <h3 style={s.h3}>Annual Plan (recommended)</h3>
        <ul style={s.ul}>
          <li style={s.li}><strong>Price:</strong> CA$39.99 per year (~CA$3.33/month)</li>
          <li style={s.li}><strong>Billing:</strong> Charged to your Apple ID at confirmation of purchase</li>
          <li style={s.li}><strong>Renewal:</strong> Automatically renews annually unless cancelled at least 24 hours before the end of the current period</li>
          <li style={s.li}><strong>Cancellation:</strong> Cancel anytime in Settings → Manage Subscription. Access continues until the end of the paid period</li>
          <li style={s.li}><strong>Refunds:</strong> Non-refundable except as required by Apple's refund policies or applicable law</li>
        </ul>
        <h3 style={s.h3}>Monthly Plan</h3>
        <ul style={s.ul}>
          <li style={s.li}><strong>Price:</strong> CA$4.99 per month</li>
          <li style={s.li}><strong>Billing:</strong> Charged to your Apple ID at confirmation of purchase</li>
          <li style={s.li}><strong>Renewal:</strong> Automatically renews monthly unless cancelled at least 24 hours before the end of the current period</li>
          <li style={s.li}><strong>Cancellation:</strong> Cancel anytime in Settings → Manage Subscription. Access continues until the end of the paid period</li>
          <li style={s.li}><strong>Refunds:</strong> Non-refundable except as required by Apple's refund policies or applicable law</li>
        </ul>

        <h2 style={s.h2}>7. Acceptable use</h2>
        <p style={s.p}>You agree not to reverse engineer, decompile, or extract the App's source code, use the App for any unlawful purpose, or attempt to gain unauthorized access to any system connected to the App.</p>

        <h2 style={s.h2}>8. Disclaimer of warranties</h2>
        <p style={s.p}>The App is provided "as is" without warranties of any kind. We do not warrant that the App will be error-free or that zakat calculations or financial projections will be accurate for your specific situation.</p>

        <h2 style={s.h2}>9. Limitation of liability</h2>
        <p style={s.p}>To the maximum extent permitted by applicable law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the App, including financial losses from reliance on App calculations. Our total liability shall not exceed the amount you paid in the 12 months preceding the claim.</p>

        <h2 style={s.h2}>10. Governing law</h2>
        <p style={s.p}>These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario, Canada.</p>

        <h2 style={s.h2}>11. Changes</h2>
        <p style={s.p}>We may update these Terms. If we make material changes, we will notify you within the App. Continued use after changes take effect constitutes acceptance.</p>

        <h2 style={s.h2}>12. Contact</h2>
        <p style={s.p}>Email: <a href="mailto:hello@barakahbudget.com" style={s.footerLink}>hello@barakahbudget.com</a></p>
      </div>
      <Footer />
    </div>
  );
}
